import { useCallback, useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import useProfileCommonStore from '../use-profile-common-store';
import { useChartMoneyMarketPriceLine } from '../../../hooks/api';
import { findClosestChartDatum } from '../../../utils/sharedHelpers';

export const TYPES = {
  liquidAssetsDaily: 'DailyLiquidAssets',
  liquidAssetsWeekly: 'WeeklyLiquidAssets',
  marketValueDaily: 'DailyMarketValue',
  netFlows: 'NetFlows'
};

const tryParseJson = json => {
  try {
    return json ? JSON.parse(json) : null;
  } catch {
    return null;
  }
};

const mapTableRows = interactions =>
  Object.keys(interactions).map(key => ({
    date: key,
    ...interactions[key]
  }));

const sortHoverData = data =>
  data.filter(datum => datum && !isNaN(datum.X)).sort((a, b) => a.X - b.X);

const useMoneyMarketChart = ({ type }) => {
  const [chartWidth, setChartWidth] = useState();

  const [hoverDatumCurrent, setHoverDatumCurrent] = useState();

  const { apiConfigBase, commonState } = useProfileCommonStore();

  const chartInteractionsParsedDefault = useRef({});

  const { client, apiType, xid } = apiConfigBase;

  const chartInputs = {
    client,
    apiType,
    shouldLoad: !!chartWidth,
    params: {
      dataType: type,
      height: 230,
      lineColor: '#0053A3',
      timeFrame: 180,
      width: chartWidth,
      xid
    }
  };

  const { data = {} } = useChartMoneyMarketPriceLine(chartInputs);

  const { image: src, chartInteractions } = data;

  const [tableRows, hoverData] = useMemo(() => {
    const chartInteractionsParsed =
      tryParseJson(chartInteractions) || chartInteractionsParsedDefault.current;
    const rows = mapTableRows(chartInteractionsParsed);
    return [rows, sortHoverData(rows)];
  }, [chartInteractions]);

  const handleHoverChart = useCallback(
    ({ x } = {}) => {
      const datum = findClosestChartDatum({
        dataSorted: hoverData,
        xCoordinate: x
      });

      setHoverDatumCurrent(datum);
    },
    [hoverData]
  );

  const valueFormat = useMemo(() => {
    const lengths = hoverData
      .map(({ value } = {}) => {
        const decimalPlaces = value && value.toString().split('.')[1];
        return decimalPlaces ? decimalPlaces.length : 0;
      })
      .filter(Boolean);

    const precision = lengths.length ? Math.max(...lengths) : 0;

    return `0,0.${Array(precision + 1).join('0')}`;
  }, [hoverData]);

  const handleHoverChartEnd = useCallback(() => setHoverDatumCurrent(), []);

  return {
    chartHeight: 230,
    fundName: _get(commonState, 'xref.data.name'),
    hoverDatum: hoverDatumCurrent || hoverData[hoverData.length - 1] || {},
    onHoverChart: handleHoverChart,
    onHoverChartEnd: handleHoverChartEnd,
    onResizeChart: setChartWidth,
    tableRows,
    src,
    valueFormat
  };
};

export default useMoneyMarketChart;
